import { useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  File,
  FileArchive,
  FileSpreadsheet,
  FileText,
  FileType,
  ImageOff,
  Paperclip,
  Presentation,
} from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { useSnackbar } from "notistack";
import { getAssetUrl } from "../api/ess";
import { apiErrorMessage } from "../utils/apiError";

interface Props {
  assetId: string;
  name?: string;
  mime?: string;
}

const TILE = 96;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

function looksLikeImage(name?: string, mime?: string) {
  if (mime?.startsWith("image/")) return true;
  if (!name) return false;
  return IMAGE_EXT.test(name);
}

// Detect an image from the presigned URL itself. Assets are stored at
// `user_assets/{uuid}_{filename}`, so the URL path carries the original
// extension — this lets us preview attachments that were stored with no
// name/mime hint (e.g. note attachments, which keep only the asset id).
function looksLikeImageUrl(url: string) {
  const path = url.split(/[?#]/)[0];
  return IMAGE_EXT.test(path);
}

// Recover the original filename from the presigned URL. The S3 key is
// `user_assets/{uuid}_{original_filename}` (see api/assets.py), and the
// uuid carries no underscore, so everything after the first underscore in
// the last path segment is the original name. Lets docs/PDFs show a real
// filename instead of the bare asset id. Exported for unit testing.
export function filenameFromUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const path = url.split(/[?#]/)[0];
    const seg = decodeURIComponent(path.split("/").pop() ?? "");
    if (!seg) return null;
    const us = seg.indexOf("_");
    const fname = us >= 0 ? seg.slice(us + 1) : seg;
    return fname || null;
  } catch {
    return null;
  }
}

// Last extension of a filename or URL path (lowercased, no dot).
function extOf(s: string | null | undefined): string | null {
  if (!s) return null;
  const clean = s.split(/[?#]/)[0];
  const m = clean.match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : null;
}

// File-type → icon. The vocabulary is small and fixed, so a switch-like
// map is fine (no registry). Unknown-with-extension falls back to a
// generic file; unknown-without-extension to a paperclip.
function fileIconFor(ext: string | null, mime?: string) {
  if (ext === "pdf" || mime === "application/pdf") return FileText;
  if (ext && ["doc", "docx", "odt", "rtf", "txt"].includes(ext))
    return FileType;
  if (ext && ["xls", "xlsx", "csv", "ods"].includes(ext)) return FileSpreadsheet;
  if (ext && ["ppt", "pptx", "odp"].includes(ext)) return Presentation;
  if (ext && ["zip", "rar", "7z", "tar", "gz"].includes(ext))
    return FileArchive;
  return ext ? File : Paperclip;
}

/**
 * A single attachment, rendered as a 96px tile: an image thumbnail when
 * the asset is an image, otherwise a file-type tile (PDF / doc / sheet /
 * … icon) captioned with the real filename. Attachments stored without a
 * name/mime hint (notes, suggestion files) resolve both their image-ness
 * and their filename from the presigned URL. Clicking opens the asset in
 * a new tab.
 */
export default function AttachmentLink({ assetId, name, mime }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [opening, setOpening] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState(false);
  const [imageBroken, setImageBroken] = useState(false);

  const couldBeImage = looksLikeImage(name, mime);
  const hasHint = !!mime || !!name;
  // Fetch the presigned URL up front when it tells us something a hint
  // can't: image-ness (to render a thumbnail) or, for hint-less
  // attachments, the real filename + extension.
  const shouldFetch = couldBeImage || !hasHint;
  const isImage = couldBeImage || (!!url && looksLikeImageUrl(url));

  useEffect(() => {
    if (!shouldFetch) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getAssetUrl(assetId);
        if (!cancelled) setUrl(res.url);
      } catch {
        if (!cancelled) setUrlError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetId, shouldFetch]);

  const resolvedName = name ?? filenameFromUrl(url);
  const ext = extOf(name) ?? extOf(url);
  // Still waiting on the URL we need to know the name/type. Don't expose
  // the raw asset id while resolving.
  const resolving = shouldFetch && !url && !urlError;

  const open = async () => {
    setOpening(true);
    try {
      const res = url ? { url } : await getAssetUrl(assetId);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      enqueueSnackbar(apiErrorMessage(err, "Could not fetch attachment"), {
        variant: "error",
      });
    } finally {
      setOpening(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

  const wrapperSx = { cursor: "pointer", maxWidth: TILE } as const;
  const ariaLabel = resolvedName ? `Open ${resolvedName}` : "Open attachment";

  // Image thumbnail.
  if (isImage && url && !imageBroken) {
    return (
      <Stack
        direction="column"
        spacing={0.5}
        alignItems="center"
        sx={wrapperSx}
        onClick={open}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
      >
        <Box
          component="img"
          src={url}
          alt={resolvedName ?? "attachment"}
          onError={() => setImageBroken(true)}
          sx={{
            width: TILE,
            height: TILE,
            borderRadius: 1.5,
            objectFit: "cover",
            border: "1px solid",
            borderColor: "divider",
            display: "block",
          }}
        />
        {resolvedName && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ maxWidth: TILE, display: "block" }}
          >
            {resolvedName}
          </Typography>
        )}
      </Stack>
    );
  }

  // Non-image (or broken image): a compact single-line chip (type icon +
  // real filename), much shorter than the image thumbnail — a doc/PDF
  // doesn't warrant a 96px tile.
  const Icon = imageBroken ? ImageOff : fileIconFor(ext, mime);
  const showSpinner = resolving || opening;
  const caption = resolvedName ?? (resolving ? "Loading…" : "Attachment");

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={open}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      sx={(theme) => ({
        cursor: "pointer",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        px: 1.25,
        py: 0.75,
        // Cap the height so it reads as a slim chip next to the 96px image
        // tiles, never a tall block.
        maxHeight: 40,
        maxWidth: 220,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: alpha(theme.palette.primary.main, 0.06),
        },
      })}
    >
      {showSpinner ? (
        <CircularProgress size={16} sx={{ flexShrink: 0 }} />
      ) : (
        <Box
          component="span"
          sx={{ color: "primary.main", display: "inline-flex", flexShrink: 0 }}
        >
          <RavenIcon icon={Icon} sx={{ fontSize: 18 }} />
        </Box>
      )}
      <Typography
        variant="body2"
        color="text.primary"
        noWrap
        sx={{ minWidth: 0 }}
      >
        {caption}
      </Typography>
    </Stack>
  );
}
